import React, {FC, useContext, useEffect} from "react";
import {SaveOutlined} from "@ant-design/icons";
import {Alert, Button, Col, Form, Input, Result, Row, App} from "antd";
import {useTranslation} from "react-i18next";
import {AuthContext} from "../../Contexts/AuthContext";
import {hasAdministratorRole} from "../../Static/OrganizerRoles";
import {
    buildGeneralSettingsUpsertInput,
    generalSettingsToFormValues
} from "./generalSettingsPageHelpers";
import type {GeneralSettingsFormValues} from "./generalSettingsPageHelpers";
import OrganizationMembersSection from "./OrganizationMembersSection";
import {buildUserFacingErrorLabels, getUserFacingErrorMessage} from "../../userFacingErrorUtils";
import {PercentInput} from "../../Components/ui/PercentInput";
import {AdminPage, PageHeader} from "../../Components/ui";
import {
    useGeneralSettingsQuery,
    useUpsertGeneralSettingsMutation
} from "../../GraphQL/generated/graphql";

const requiredRule = (messageText: string) => ({required: true, message: messageText});

export const GeneralSettingsPage: FC = () => {
    const {message} = App.useApp();
    const {t} = useTranslation();
    const {auth} = useContext(AuthContext);
    const isAdministrator = hasAdministratorRole(auth.roles);
    const [form] = Form.useForm<GeneralSettingsFormValues>();
    const errorLabels = buildUserFacingErrorLabels(t);

    const settingsQuery = useGeneralSettingsQuery({
        skip: !isAdministrator,
        fetchPolicy: "cache-and-network"
    });
    const [upsertGeneralSettings, {loading: saving}] = useUpsertGeneralSettingsMutation();

    const settings = settingsQuery.data?.generalSettings;

    useEffect(() => {
        if (settings) {
            form.setFieldsValue(generalSettingsToFormValues(settings));
        }
    }, [form, settings]);

    const handleSubmit = async () => {
        const values = await form.validateFields();

        try {
            const result = await upsertGeneralSettings({
                variables: {
                    input: buildGeneralSettingsUpsertInput(values)
                }
            });

            const updated = result.data?.upsertGeneralSettings;
            if (updated) {
                form.setFieldsValue(generalSettingsToFormValues(updated));
            }

            message.success(t("general-settings-saved", {defaultValue: "General settings saved"}));
            await settingsQuery.refetch();
        } catch (error: any) {
            message.error(getUserFacingErrorMessage(error, t, errorLabels));
        }
    };

    if (!isAdministrator) {
        return (
            <AdminPage>
                <PageHeader title={t("general-settings", {defaultValue: "Organization settings"})}/>
                <Result
                    status="403"
                    title={t("not-authorized", {defaultValue: "Not authorized"})}
                    subTitle={t("administrator-only-page", {defaultValue: "This page is available to administrators only."})}
                />
            </AdminPage>
        );
    }

    return (
        <AdminPage>
            <PageHeader
                title={t("general-settings", {defaultValue: "Organization settings"})}
                actions={(
                    <Button
                        type="primary"
                        icon={<SaveOutlined/>}
                        onClick={handleSubmit}
                        loading={saving}
                    >
                        {t("save", {defaultValue: "Save"})}
                    </Button>
                )}
            />
            <div className="ati-stack">

                {settingsQuery.error && (
                    <Alert
                        type="error"
                        showIcon
                        message={t("error-loading-items", {defaultValue: "Error loading items"})}
                        description={getUserFacingErrorMessage(settingsQuery.error, t, errorLabels)}
                    />
                )}

                <Form form={form} layout="vertical" disabled={settingsQuery.loading && !settings}>
                    <Row gutter={16}>
                        <Col xs={24} md={12}>
                            <Form.Item
                                name="payeeName"
                                label={t("company-payee-name", {defaultValue: "Company/payee name"})}
                                rules={[requiredRule(t("payee-name-required", {defaultValue: "Company/payee name is required."}))]}
                            >
                                <Input autoComplete="organization"/>
                            </Form.Item>
                        </Col>
                        <Col xs={24} md={12}>
                            <Form.Item
                                name="payeeOib"
                                label={t("oib", {defaultValue: "OIB"})}
                                rules={[requiredRule(t("payee-oib-required", {defaultValue: "OIB is required."}))]}
                            >
                                <Input autoComplete="off"/>
                            </Form.Item>
                        </Col>
                        <Col xs={24}>
                            <Form.Item
                                name="payeeAddress"
                                label={t("payee-address", {defaultValue: "Payee address"})}
                                rules={[requiredRule(t("payee-address-required", {defaultValue: "Payee address is required."}))]}
                            >
                                <Input autoComplete="street-address"/>
                            </Form.Item>
                        </Col>
                        <Col xs={24} md={12}>
                            <Form.Item
                                name="payeeIban"
                                label={t("iban", {defaultValue: "IBAN"})}
                                rules={[requiredRule(t("payee-iban-required", {defaultValue: "IBAN is required."}))]}
                            >
                                <Input autoComplete="off"/>
                            </Form.Item>
                        </Col>
                        <Col xs={24} md={12}>
                            <Form.Item
                                label={t("registration-vat-default", {defaultValue: "Default participation VAT"})}
                                required
                            >
                                <Form.Item
                                    name="registrationVatPercent"
                                    noStyle
                                    rules={[requiredRule(t("registration-vat-required", {defaultValue: "Registration VAT is required."}))]}
                                >
                                    <PercentInput/>
                                </Form.Item>
                            </Form.Item>
                        </Col>
                        <Col xs={24} md={12}>
                            <Form.Item
                                label={t("accommodation-ppo-default", {defaultValue: "Default accommodation PPO"})}
                                required
                            >
                                <Form.Item
                                    name="accommodationPpoPercent"
                                    noStyle
                                    rules={[requiredRule(t("accommodation-ppo-required", {defaultValue: "Accommodation PPO is required."}))]}
                                >
                                    <PercentInput/>
                                </Form.Item>
                            </Form.Item>
                        </Col>
                    </Row>
                </Form>

                <OrganizationMembersSection/>
            </div>
        </AdminPage>
    );
};

export default GeneralSettingsPage;
